import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { SUPPORTED_BROWSERS } from '@frontend/shared';
import { captureEvent } from '@sentry/browser';

declare global {
  interface Window {
    initApp: () => void;
  }
  interface Navigator {
    sayswho?: string[];
  }
}

function initApp() {
  bootstrapApplication(AppComponent, appConfig).catch((err) => console.error(err));
}

// Helper function to detect operating system
function detectOS(ua: string): string {
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua) || /ios/i.test(ua)) return 'ios';
  if (/Win(dows )?/i.test(ua)) return 'windows';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'osx';
  if (/Linux/i.test(ua)) return 'linux';
  return '-?';
}

// Helper function to check for iOS WebView
function detectIOSWebView(ua: string, os: string): string[] | null {
  if (os !== 'ios' || ua.includes('Safari/')) return null;

  const iOSVersion = /OS (\d+)_(\d+)/.exec(ua);
  if (!iOSVersion?.[1]) return null;

  // Treat WebView as Safari for compatibility purposes
  return ['Safari', iOSVersion[1], os];
}

// Helper function to detect Internet Explorer
function detectIE(ua: string, os: string): string[] | null {
  if (!/trident/i.test(ua)) return null;

  const tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
  return ['IE', tem[1] ?? '', os];
}

// Helper function to detect Chrome-based browsers
function detectChromeBased(ua: string, os: string): string[] | null {
  const operaMatch = /\b(OPR)\/(\d+)/.exec(ua);
  if (operaMatch) return ['Opera', operaMatch[2] ?? '', os];

  const edgeMatch = /\b(Edg[a-zA-Z]{0,3})\/(\d+)/.exec(ua);
  if (edgeMatch) return ['Edge', edgeMatch[2] ?? '', os];

  return null;
}

// Helper function to detect Safari-based browsers
function detectSafariBased(ua: string, os: string): string[] | null {
  const edgeIOSMatch = /\b(EdgiOS)\/(\d+)/.exec(ua);
  if (edgeIOSMatch) return ['Edge', edgeIOSMatch[2] ?? '', os];

  const firefoxIOSMatch = /\b(FxiOS)\/(\d+)/.exec(ua);
  if (firefoxIOSMatch) return ['Firefox', firefoxIOSMatch[2] ?? '', os];

  const chromeIOSMatch = /\b(CriOS)\/(\d+)/.exec(ua);
  if (chromeIOSMatch) return ['Chrome', chromeIOSMatch[2] ?? '', os];

  return null;
}

// Helper function to apply version override for Safari-based browsers
function applyVersionOverride(ua: string, result: string[]): string[] {
  const isWebView = /\bwv\b/.test(ua);
  const isChrome = result[0] === 'Chrome' || /chrome/i.test(ua);

  if (isWebView || isChrome) return result;

  const versionMatch = /version\/(\d+)/i.exec(ua);
  if (versionMatch?.[1]) {
    result[1] = versionMatch[1];
  }

  return result;
}

navigator.sayswho = (function () {
  const ua = navigator.userAgent;
  // Android webview test: Mozilla/5.0 (Linux; Android 16; sdk_gphone64_arm64 Build/BP22.250325.006; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/133.0.6943.137 Mobile Safari/537.36';
  // iOS webview test: 'Mozilla/5.0 (iPhone; CPU iPhone OS 15_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148oeamtc_hybrid/oeamtc_app_ios/6.8.1-60801004';

  const M = /(opera|chrome|safari|firefox|msie|trident)\/?\s*(\d+)/i.exec(ua) || [];
  const m1 = M[1] ?? '';
  const m2 = M[2] ?? '';
  const os = detectOS(ua);

  // Check for iOS WebView first
  const iosWebView = detectIOSWebView(ua, os);
  if (iosWebView) return iosWebView;

  // Check for Internet Explorer
  const ieResult = detectIE(ua, os);
  if (ieResult) return ieResult;

  // Check for Chrome-based browsers
  if (m1 === 'Chrome') {
    const chromeBasedResult = detectChromeBased(ua, os);
    if (chromeBasedResult) return chromeBasedResult;
  }

  // Check for Safari-based browsers
  if (m1 === 'Safari') {
    const safariBasedResult = detectSafariBased(ua, os);
    if (safariBasedResult) return safariBasedResult;
  }

  // Default result
  const browserName = m1 || 'Unknown';
  const browserVersion = m2 || '0';
  const result = [browserName, browserVersion, os];

  // Apply version override for Safari-based browsers
  return applyVersionOverride(ua, result);
})();

if (document.readyState === 'loading') {
  window.addEventListener('DOMContentLoaded', startBrowserCheck);
} else {
  startBrowserCheck();
}

function startBrowserCheck() {
  try {
    const browser = navigator.sayswho;
    console.log('browser', browser);
    if (browser && browser.length > 0) {
      const browserVersion = browser[1];
      const browserName = browser[0]?.toLowerCase();
      const browserOs = browser[2]?.toLowerCase();

      if (browserOs && Object.hasOwn(SUPPORTED_BROWSERS, browserOs)) {
        // Get the browser group (android, ios, desktop)
        const browserGroup = SUPPORTED_BROWSERS[browserOs as keyof typeof SUPPORTED_BROWSERS];
        // Get the minimum supported version for this browser, if it exists
        const minSupportedVersion =
          browserGroup && (browserGroup as Record<string, string>)[browserName ?? ''];
        // Compare as numbers if possible
        if (
          minSupportedVersion &&
          browserVersion &&
          !isNaN(Number(browserVersion)) &&
          !isNaN(Number(minSupportedVersion)) &&
          Number(browserVersion) >= Number(minSupportedVersion)
        ) {
          initApp();
        } else {
          showOutdatedBrowserWarning(browser);
        }
      } else {
        showOutdatedBrowserWarning(browser);
      }
    }
  } catch (error) {
    console.error('Error initializing application:', error);
  }
}

function showOutdatedBrowserWarning(browser: string[]) {
  const warningHtml = `
            <div id="outdated-browser-warning" class="warning-message" aria-live="polite" role="alert"
              style="background-color:#fefce9;color:#ca8a04;border:1px solid #fef08a;border-radius:6px;display:flex;align-items:center;padding:0.5rem 0.625rem;margin:1rem;">
              <div class="warning-message-content">
                <p class="warning-message-title" style="font-weight:600;margin:0 0 0.5rem">Warn Message</p>
                <p class="warning-message-title" style="margin:0 0 1rem">
                  Your browser is outdated, we can't guarantee that the application will work correctly.
                </p>
                <button
                  type="button"
                  id="load-anyway-link"
                  style="margin:0;color:blue;cursor:pointer;text-decoration:underline;background:none;border:none;padding:0;font:inherit;text-align:left"
                >
                  Click here to load it anyway
                </button>
              </div>
            </div>
          `;
  document.body.insertAdjacentHTML('beforeend', warningHtml);
  console.warn('Browser is outdated:', browser);

  // Add event listener to the load anyway link
  document.getElementById('load-anyway-link')?.addEventListener('click', () => {
    captureEvent({
      message: 'User clicked "load anyway" on outdated browser warning',
      level: 'info',
      extra: {
        browser: navigator.sayswho,
      },
    });
    document.getElementById('outdated-browser-warning')?.remove();
    initApp();
  });
}
